import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { makeStyles, Typography } from '@material-ui/core'
import { KeyboardTimePicker, KeyboardTimePickerProps } from '@material-ui/pickers'
import { AccessTime } from '@material-ui/icons'
import { gray } from '../../utils/colors'
import { FONT_WEIGHTS } from '../../utils'
import StyledIconWithTooltip from './StyledIconWithTooltip'

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		'& .MuiTextField-root': {
			minWidth: 'unset',
			'& .MuiFilledInput-underline': {
				minWidth: 'unset'
			},
			'& .MuiSvgIcon-root': {
				fill: gray[700]
			},
			'& .MuiFilledInput-adornedEnd': {
				paddingRight: 0
			}
		}
	},
	headerContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem'
	},
	header: {
		fontFamily: 'Nunito',
		fontSize: '0.75rem',
		color: gray[900],
		textTransform: 'uppercase',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		letterSpacing: '0.0625rem'
	}
}))

/**
 * A StyledKeyboardTimePicker that handles display with moment, but returns a formatted string and date via the timeChanged callback
 * All KeyboardTimePickerProps can be passed in as well, however value, onChange, inputVariant and keyboardIcon cannot be overridden
 * @param {KeyboardTimePickerProps & {timeChanged: (e: {formatted: 'HH:mm', data: moment}) => any, initialvalue: 'HH:mm' | null, header?: string, tooltip?: {tooltipChildren?: JSX.Element, tooltipText?: string, faIcon?: string}}} props
 * @returns
 */
const StyledKeyboardTimePicker = (props) => {
	const classes = useStyles()
	const [dateForDisplay, setDateForDisplay] = useState(null)

	const { timeChanged, initialvalue } = props

	useEffect(() => {
		if (initialvalue) {
			setDateForDisplay(moment(initialvalue, 'HH:mm'))
		}
		if (initialvalue === null || initialvalue === undefined || initialvalue === '') {
			setDateForDisplay(null)
		}
	}, [initialvalue])

	const handleTimeChange = (date) => {
		setDateForDisplay(date)
		if (timeChanged) {
			let formattedDate = date?.format('HH:mm') || null
			if (formattedDate === 'Invalid date') {
				formattedDate = null
			}

			timeChanged({ formatted: formattedDate, date: date })
		}
	}

	const propsToPassOn = { ...props }
	if (propsToPassOn.timeChanged) {
		delete propsToPassOn.timeChanged
	}
	if (propsToPassOn.initialvalue) {
		delete propsToPassOn.initialvalue
	}

	return (
		<div className={classes.root}>
			{props.header ? (
				props.tooltip && !props.disabled ? (
					<span className={classes.headerContainer}>
						<Typography variant="overline">{props.header}</Typography>
						<StyledIconWithTooltip
							tooltipChildren={props.tooltip.tooltipChildren || undefined}
							tooltipText={props.tooltip.tooltipText || ''}
							faIcon={props.tooltip.faIcon || undefined}
							skiptownvariant={props.tooltip.skiptownvariant || undefined}
						/>
					</span>
				) : (
					<Typography variant="overline">{props.header}</Typography>
				)
			) : null}
			<KeyboardTimePicker
				{...propsToPassOn}
				initialFocusedDate={null}
				inputVariant="filled"
				keyboardIcon={<AccessTime />}
				value={dateForDisplay}
				onChange={handleTimeChange}
			/>
		</div>
	)
}

export default StyledKeyboardTimePicker
